// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-18) — the region through time, in Analyze's
 * Statistics tab under the area over a threshold
 * (docs/AYNI_XR_WHEN_EXTREMES_PLAN.md, step 2; the arithmetic is
 * `services/research/regionSeries.ts`).
 *
 * "When did we get the highest or lowest measurement" for a box, a
 * latitude band, a country or the whole globe: the region's mean, highest
 * and lowest are measured in every frame as playback passes it (or *Read
 * the missing frames* steps through them), drawn as three lines on one
 * graph, and the block under it names the frame of the record high and
 * low (and where in the region) and of the highest and lowest mean, each
 * date a button that plays from that frame.
 *
 * Recorded the way the area over a threshold is (`thresholdUI.ts`): one
 * result per frame, keyed by the frame, so a replay overwrites; it ends
 * with the panel, a new dataset or "Clear", and starts again when the
 * region changes. Gated as the other research tools (decision R2).
 */

import type { AnalyzeSource, AnalyzeTimeAxis } from './analyzeUI'
import type { LumaSnapshot } from '../services/glLumaSampler'
import type { LatLonBounds, TexelWindow } from '../services/datasetStats'
import type { DatasetOverlayOptions } from '../types'
import type { DisplayColorScale } from '../types/unit-scale'
import { releaseOf } from '../types/color-scale'
import { buildSeriesChart } from '../services/measure/chartGeometry'
import { valueTable } from '../services/research/changeMap'
import { gridKey } from '../services/research/baseline'
import { quantisationStep } from '../services/research/hovmoller'
import { stepFrames } from '../services/research/periodRun'
import { trail, trailOn } from '../services/research/researchTrail'
import { cellRowAreasKm2, frameAtFraction, frameStampMs, missingThresholdFrames, recordThresholdFrame, skippedThresholdFrames } from '../services/research/threshold'
import {
  REGION_SERIES_METHOD,
  buildRegionCsvText,
  createRegionSeries,
  measureRegion,
  regionChartSeries,
  regionRecords,
  type RegionFrame,
  type RegionFrameResult,
  type RegionRecord,
  type RegionSeries,
} from '../services/research/regionSeries'
import { analysisExportsLocked, researchToolsAllowed, researchUpsellShown } from '../services/planAccess'
import { formatStatValue } from './analyzeCharts'
import { downloadCsv } from './analyzeExport'
import { escapeHtml } from './domUtils'
import { ayniCsvFilename } from './pinsCsv'
import { openPlans } from './xrPlansUI'
import { t } from '../i18n'
import { formatDate, formatNumber } from '../i18n/format'
import { frameIsoForFile, researchFrameAxis, researchPrecision } from '../services/research/frameTime'
import { compassWhere, fillWhenHost, renderWhenBlock, type WhenRow } from './whenExtremesUI'

type AnalyzeFrame = { snapshot: LumaSnapshot; scale: DisplayColorScale; options: DatasetOverlayOptions }

/** What the Statistics tab hands the section: the frame and the region its numbers cover. */
export interface RegionSeriesContext {
  src: AnalyzeSource
  frame: AnalyzeFrame
  /** The region's cells (undefined: the whole dataset), as the statistics use them. */
  window: TexelWindow | undefined
  /** `baseline.regionKey`: equal keys, the same cells. */
  regionKey: string
  /** The region as the picker names it. */
  regionLabel: string
  /** The region in English, for the CSV and the record. */
  regionEnglish: string
  regionBounds: LatLonBounds | null
  /** For a country: where its outline comes from (the CSV says). */
  regionShape?: string | null
}

interface Recorder {
  readonly key: string
  readonly datasetId: string | null
  readonly gridKey: string
  readonly width: number
  readonly height: number
  readonly window: TexelWindow | undefined
  readonly rowAreas: Float64Array
  readonly values: Float64Array
  readonly options: DatasetOverlayOptions
  readonly scale: DisplayColorScale
  readonly regionLabel: string
  readonly regionEnglish: string
  readonly regionBounds: LatLonBounds | null
  readonly regionShape: string | null
  readonly axis: AnalyzeTimeAxis | null
  readonly series: RegionSeries | null
  stop: (() => void) | null
  stepping: boolean
  cancel: boolean
}

interface View {
  host: HTMLElement
  status: HTMLElement | null
  plot: HTMLElement | null
  readout: HTMLElement | null
  stepBtn: HTMLButtonElement | null
  when: HTMLElement | null
}

let recorder: Recorder | null = null
/**
 * Following is started by the reader ("Follow through time"): each frame
 * read costs a read of the whole frame from the graphics card, which is
 * not spent on every Analyze panel opened on a stream. It stays on while
 * the panel is open, across regions.
 */
let armed = false
let view: View | null = null
let current: RegionSeriesContext | null = null
let cursor: number | null = null
let said: { text: string; at: number } | null = null
let drawQueued = false

/** The graph's box, as the area over a threshold draws it. */
const G = { w: 390, h: 150, left: 40, right: 6, top: 10, plot: 108, railTop: 124, rail: 3 }
const BRIDGE = 6

/** Stop recording and forget the series: the panel closed, the dataset changed, or everything was cleared. */
export function resetRegionSeries(): void {
  armed = false
  recorder?.stop?.()
  if (recorder) recorder.cancel = true
  recorder = null
  view = null
  current = null
  cursor = null
  said = null
}

/** For tests and the chatbot: the series as it stands. */
export function regionSeries(): RegionSeries | null {
  return recorder?.series ?? null
}

/**
 * AYNI: the records so far, in plain numbers (the chatbot, the methods
 * paragraph): the region, how much was read, and each record's value, frame
 * date and place; null before any frame with data.
 */
export function regionSeriesSummary(): {
  region: string
  units: string | null
  framesRead: number
  frames: number
  high: RegionRecord
  low: RegionRecord
  highMean: RegionRecord | null
  lowMean: RegionRecord | null
} | null {
  const rec = recorder
  if (!rec?.series || !researchToolsAllowed()) return null
  const r = regionRecords(rec.series)
  if (!r) return null
  return { region: rec.regionEnglish, units: rec.scale.units ?? null, framesRead: rec.series.read, frames: rec.series.total, ...r }
}

function para(cls: string, text: string): HTMLParagraphElement {
  const p = document.createElement('p')
  p.className = cls
  p.textContent = text
  return p
}

function button(text: string, cls: string, onClick: (b: HTMLButtonElement) => void): HTMLButtonElement {
  const b = document.createElement('button')
  b.type = 'button'
  b.className = cls
  b.textContent = text
  b.addEventListener('click', () => onClick(b))
  return b
}

/** A frame's date on the axis, as fine as the axis needs (UTC), as the area over a threshold labels it. */
function timeLabel(ms: number, spanMs: number): string {
  const day = 86_400_000
  const utc = { timeZone: 'UTC' } as const
  const precision = researchPrecision()
  if (precision === 'year') return formatDate(ms, { ...utc, year: 'numeric' })
  if (precision === 'month' || spanMs > 20 * 365 * day) return formatDate(ms, { ...utc, year: 'numeric', month: 'short' })
  if (spanMs <= 2 * day) return formatDate(ms, { ...utc, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  if (spanMs <= 400 * day) return formatDate(ms, { ...utc, month: 'short', day: 'numeric' })
  return formatDate(ms, { ...utc, year: 'numeric', month: 'short' })
}

function frameInput(snapshot: LumaSnapshot, rec: Recorder): RegionFrame {
  return { codes: snapshot.data, width: snapshot.width, height: snapshot.height, values: rec.values, rowAreas: rec.rowAreas, window: rec.window, options: rec.options }
}

function ensureRecorder(ctx: RegionSeriesContext): Recorder {
  const { snapshot, scale, options } = ctx.frame
  const grid = gridKey(snapshot.width, snapshot.height, options)
  const datasetId = ctx.src.datasetId()
  const key = [datasetId, grid, ctx.regionKey].join('|')
  if (recorder?.key === key) return recorder
  recorder?.stop?.()
  if (recorder) recorder.cancel = true
  recorder = null
  cursor = null
  const axis = ctx.src.timeAxis?.() ?? null
  const rec: Recorder = {
    key,
    datasetId,
    gridKey: grid,
    width: snapshot.width,
    height: snapshot.height,
    window: ctx.window,
    rowAreas: cellRowAreasKm2(snapshot.width, snapshot.height, options),
    values: valueTable(scale),
    options,
    scale,
    regionLabel: ctx.regionLabel,
    regionEnglish: ctx.regionEnglish,
    regionBounds: ctx.regionBounds,
    regionShape: ctx.regionShape ?? null,
    axis,
    series: axis ? createRegionSeries(axis, axis.usable ? (i) => axis.usable!(i) : undefined) : null,
    stop: null,
    stepping: false,
    cancel: false,
  }
  recorder = rec
  if (axis && rec.series) rec.stop = axis.onFrame((i) => readFrame(rec, i))
  return rec
}

/** Read the frame on the globe into the series, if it is still this recorder's dataset and grid. */
function readFrame(rec: Recorder, frame: number): void {
  if (recorder !== rec || !rec.series) return
  if (!view?.host.isConnected) {
    resetRegionSeries()
    return
  }
  const src = current?.src
  if (!src || src.datasetId() !== rec.datasetId) return
  const f = src.frame()
  if (!f || gridKey(f.snapshot.width, f.snapshot.height, f.options) !== rec.gridKey) return
  recordThresholdFrame(rec.series, frame, measureRegion(frameInput(f.snapshot, rec)))
  queueDraw()
}

function queueDraw(): void {
  if (drawQueued) return
  drawQueued = true
  const run = (): void => {
    drawQueued = false
    paintSeries()
  }
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run)
  else setTimeout(run, 16)
}

// --- the graph -----------------------------------------------------------------

function xOf(frac: number): number {
  return G.left + frac * (G.w - G.left - G.right)
}

function fracOfFrame(series: RegionSeries, frame: number): number {
  const last = series.results.length - 1
  return last > 0 ? frame / last : 0
}

function tick(value: number): string {
  // i18n-exempt: a number on the graph's axis; the units are drawn once above it
  return formatNumber(value, { maximumSignificantDigits: 3 })
}

function chartMarkup(rec: Recorder): string {
  const series = rec.series!
  const mean = regionChartSeries(series, 'mean')
  const max = regionChartSeries(series, 'max')
  const min = regionChartSeries(series, 'min')
  if (!mean || !max || !min) return ''
  const chart = buildSeriesChart([
    { id: 'max', color: 'max', series: max },
    { id: 'min', color: 'min', series: min },
    { id: 'mean', color: 'mean', series: mean },
  ], { bridge: BRIDGE })
  const plotW = G.w - G.left - G.right
  const bottom = G.top + G.plot
  const span = mean.endMs - mean.startMs
  const parts: string[] = []
  parts.push(`<line x1="${G.left}" y1="${G.top}" x2="${G.left}" y2="${bottom}" class="tools-data-axis"/>`)
  parts.push(`<line x1="${G.left}" y1="${bottom}" x2="${G.w - G.right}" y2="${bottom}" class="tools-data-axis"/>`)
  const units = rec.scale.units ?? ''
  if (units) parts.push(`<text x="${G.left + 3}" y="${G.top + 4}" class="tools-data-tick tools-data-units">${escapeHtml(units)}</text>`)
  if (chart) {
    parts.push(`<text x="${G.left - 4}" y="${G.top + 4}" text-anchor="end" class="tools-data-tick">${escapeHtml(tick(chart.max))}</text>`)
    parts.push(`<text x="${G.left - 4}" y="${bottom}" text-anchor="end" class="tools-data-tick">${escapeHtml(tick(chart.min))}</text>`)
    const stroke: Record<string, string> = { max: '#f97316', min: '#38bdf8', mean: '' }
    for (const tr of chart.traces) {
      const style = tr.id === 'mean' ? '' : ` style="stroke:${stroke[tr.id]};stroke-width:1.1;stroke-opacity:0.85"`
      for (const seg of tr.segments) {
        const d = seg.map((p, i) => `${i ? 'L' : 'M'}${(G.left + p.x * plotW).toFixed(1)},${(G.top + p.y * G.plot).toFixed(1)}`).join('')
        parts.push(`<path d="${d}" class="analyze-rs-trace analyze-rs-${tr.id}"${style}/>`)
      }
      for (const p of tr.dots) parts.push(`<circle cx="${(G.left + p.x * plotW).toFixed(1)}" cy="${(G.top + p.y * G.plot).toFixed(1)}" r="1.6" class="analyze-rs-dot"${tr.id === 'mean' ? '' : ` style="fill:${stroke[tr.id]}"`}/>`)
    }
    const meanTrace = chart.traces.find((tr) => tr.id === 'mean')!
    const railW = plotW / chart.slots
    meanTrace.rail.forEach((state, s) => {
      if (state === 'unsampled') return
      parts.push(`<rect x="${(G.left + s * railW).toFixed(1)}" y="${G.railTop}" width="${Math.max(0.6, railW).toFixed(2)}" height="${G.rail}" class="analyze-rs-rail${state === 'nodata' ? ' analyze-rs-rail-nodata' : ''}"/>`)
    })
    // The record high and low, marked where they sit.
    const records = regionRecords(series)
    const range = chart.max - chart.min
    if (records) {
      for (const [r, cls] of [[records.high, 'is-max'], [records.low, 'is-min']] as const) {
        const x = xOf(fracOfFrame(series, r.frame))
        const y = G.top + (range > 0 ? 1 - (r.value - chart.min) / range : 0.5) * G.plot
        parts.push(`<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3.4" class="analyze-rs-mark ${cls}"/>`)
      }
    }
  }
  const now = rec.axis?.currentFrame() ?? null
  if (now !== null) {
    const x = xOf(fracOfFrame(series, now)).toFixed(1)
    parts.push(`<line x1="${x}" y1="${G.top}" x2="${x}" y2="${bottom}" class="tools-data-playhead"/>`)
  }
  if (cursor !== null) {
    const x = xOf(fracOfFrame(series, cursor)).toFixed(1)
    parts.push(`<line x1="${x}" y1="${G.top}" x2="${x}" y2="${bottom}" class="tools-data-hover-line analyze-thr-cursor"/>`)
  }
  const labelY = G.railTop + G.rail + 10
  parts.push(`<text x="${G.left}" y="${labelY}" class="tools-data-tick">${escapeHtml(timeLabel(mean.startMs, span))}</text>`)
  parts.push(`<text x="${G.w - G.right}" y="${labelY}" text-anchor="end" class="tools-data-tick">${escapeHtml(timeLabel(mean.endMs, span))}</text>`)
  return `<svg class="tools-data-chart-svg analyze-rs-svg" viewBox="0 0 ${G.w} ${labelY + 3}" aria-hidden="true">${parts.join('')}</svg>`
}

function value(v: number | null, rec: Recorder): string {
  return v === null ? t('analyze.stat.none') : formatStatValue(v, rec.scale.units)
}

function describeFrame(rec: Recorder, frame: number): string {
  const series = rec.series!
  const span = frameStampMs(series.axis, series.results.length - 1) - series.axis.startMs
  const time = timeLabel(frameStampMs(series.axis, frame), span)
  const r: RegionFrameResult | null = series.results[frame]
  if (series.skip[frame]) return t('research.thr.readout.skipped', { time })
  if (!r) return t('research.thr.readout.unread', { time })
  if (r.cellsWithData === 0) return t('research.thr.readout.noData', { time })
  return t('research.rs.readout', { time, mean: value(r.mean, rec), max: value(r.max, rec), min: value(r.min, rec) })
}

function paintStatus(rec: Recorder): void {
  const v = view
  const series = rec.series
  if (!v?.status || !series) return
  const skipped = skippedThresholdFrames(series)
  v.status.textContent = t('research.thr.progress', { read: formatNumber(series.read), total: formatNumber(series.total) })
    + (skipped ? ` ${t('research.thr.skipped', { count: formatNumber(skipped) })}` : '')
  if (v.stepBtn) {
    const running = rec.stepping && !rec.cancel
    const missing = series.read < series.total
    v.stepBtn.hidden = !missing && !running
    v.stepBtn.textContent = running ? t('research.thr.stop') : t('research.thr.step')
    v.stepBtn.classList.toggle('analyze-thr-stop', running)
  }
}

/** The records: when the region held its highest and lowest value (and where), and its highest and lowest mean. */
function paintWhen(rec: Recorder): void {
  const v = view
  const series = rec.series
  if (!v?.when || !series) return
  const r = regionRecords(series)
  const sig = r ? [r.high.frame, r.high.value, r.high.others, r.low.frame, r.low.value, r.low.others, r.highMean?.frame, r.highMean?.value, r.lowMean?.frame, r.lowMean?.value, series.read, series.total].join('|') : 'none'
  fillWhenHost(v.when, sig, () => {
    if (!r) return null
    const span = frameStampMs(series.axis, series.results.length - 1) - series.axis.startMs
    const row = (kind: string, label: string, x: RegionRecord): WhenRow => ({
      kind,
      label,
      value: formatStatValue(x.value, rec.scale.units),
      time: timeLabel(x.timeMs, span),
      ties: x.others,
      where: x.at ? (x.cells && x.cells > 1
        ? t('research.rs.whereCells', { where: compassWhere(x.at.lat, x.at.lon), count: formatNumber(x.cells - 1) })
        : t('tools.data.when.at', { where: compassWhere(x.at.lat, x.at.lon) })) : null,
      onSeek: rec.axis ? () => {
        showCursor(x.frame)
        seekFrame(x.frame)
      } : null,
    })
    const rows: WhenRow[] = [row('max', t('research.rs.high'), r.high), row('min', t('research.rs.low'), r.low)]
    if (r.highMean) rows.push(row('mean-max', t('research.rs.highMean'), r.highMean))
    if (r.lowMean) rows.push(row('mean-min', t('research.rs.lowMean'), r.lowMean))
    return renderWhenBlock(
      t('research.rs.when.title', { region: rec.regionLabel }),
      rows,
      series.read < series.total ? t('tools.data.when.partial', { read: formatNumber(series.read), total: formatNumber(series.total) }) : t('research.rs.when.all'),
    )
  })
}

function paintSeries(): void {
  const rec = recorder
  const v = view
  if (!rec?.series || !v?.plot || !v.host.isConnected) return
  v.plot.innerHTML = chartMarkup(rec)
  paintStatus(rec)
  paintWhen(rec)
  if (v.readout && cursor !== null) v.readout.textContent = describeFrame(rec, cursor)
  trailRegion(rec)
}

function frameAtPointer(e: MouseEvent): number | null {
  const rec = recorder
  const svg = view?.plot?.querySelector('svg')
  if (!rec?.series || !svg) return null
  const r = svg.getBoundingClientRect()
  if (!(r.width > 0)) return null
  const x = ((e.clientX - r.left) / r.width) * G.w
  const frac = (x - G.left) / (G.w - G.left - G.right)
  if (frac < -0.03 || frac > 1.03) return null
  return frameAtFraction(rec.series, frac)
}

function showCursor(frame: number | null): void {
  cursor = frame
  paintSeries()
  const rec = recorder
  if (view?.readout && rec?.series) view.readout.textContent = frame === null ? t('research.thr.readoutHint') : describeFrame(rec, frame)
}

function seekFrame(frame: number): void {
  const rec = recorder
  if (!rec?.series || !rec.axis) return
  const ms = frameStampMs(rec.series.axis, frame)
  const seek = current?.src.hovmoller?.()?.seek
  if (seek) seek(ms)
  else void rec.axis.showFrame(frame)
  const span = frameStampMs(rec.series.axis, rec.series.results.length - 1) - rec.series.axis.startMs
  const text = t('research.thr.seeked', { time: timeLabel(ms, span) })
  said = { text, at: Date.now() }
  if (view?.readout) view.readout.textContent = text
}

function onPlotKey(e: KeyboardEvent): void {
  const rec = recorder
  if (!rec?.series) return
  const last = rec.series.results.length - 1
  const at = cursor ?? Math.max(0, rec.series.lastFrame)
  const page = Math.max(1, Math.round(last / 10))
  let next: number | null = null
  switch (e.key) {
    case 'ArrowLeft': next = Math.max(0, at - 1); break
    case 'ArrowRight': next = Math.min(last, at + 1); break
    case 'PageUp': next = Math.max(0, at - page); break
    case 'PageDown': next = Math.min(last, at + page); break
    case 'Home': next = 0; break
    case 'End': next = last; break
    case 'Enter':
    case ' ':
      e.preventDefault()
      seekFrame(at)
      return
    default:
      return
  }
  e.preventDefault()
  showCursor(next)
}

async function stepMissing(rec: Recorder): Promise<void> {
  const axis = rec.axis
  if (!axis || !rec.series || rec.stepping) return
  rec.stepping = true
  rec.cancel = false
  paintStatus(rec)
  await stepFrames(
    missingThresholdFrames(rec.series),
    (frame) => axis.showFrame(frame),
    (frame) => {
      if (!rec.series?.results[frame]) readFrame(rec, frame)
    },
    () => rec.cancel || recorder !== rec,
  )
  rec.stepping = false
  if (recorder === rec) paintStatus(rec)
}

/** A research record, when one is recording, keeps the records so far (one entry per region, replaced as it fills). */
function trailRegion(rec: Recorder): void {
  if (!trailOn() || !rec.series) return
  const r = regionRecords(rec.series)
  if (!r) return
  const units = rec.scale.units ?? ''
  const time = (ms: number): string => (researchFrameAxis() ? frameIsoForFile(ms) : new Date(ms).toISOString())
  const rec4 = (x: RegionRecord | null) => (x ? { value: x.value, time: time(x.timeMs), otherFrames: x.others, ...(x.at ? { lat: x.at.lat, lon: x.at.lon, cells: x.cells } : {}) } : null)
  trail({
    type: 'regionSeries',
    coalesce: `regionSeries|${rec.key}`,
    data: {
      region: rec.regionEnglish,
      ...(rec.regionBounds ? { bounds: { ...rec.regionBounds } } : {}),
      units,
      grid: `${rec.width} x ${rec.height}`,
      framesRead: rec.series.read,
      frames: rec.series.total,
      high: rec4(r.high),
      low: rec4(r.low),
      highMean: rec4(r.highMean),
      lowMean: rec4(r.lowMean),
      method: REGION_SERIES_METHOD,
      summary: `${rec.regionEnglish}: highest ${r.high.value} ${units} on ${time(r.high.timeMs)}, lowest ${r.low.value} ${units} on ${time(r.low.timeMs)}; ${rec.series.read} of ${rec.series.total} frames read`, // i18n-exempt: record text, English like the CSV
    },
  })
}

// --- the section ------------------------------------------------------------------

/**
 * Draw the section into `host` (the Statistics tab, under the area over a
 * threshold). Called whenever the statistics are computed; the recording
 * outlives redraws for as long as the dataset, grid and region are the same.
 */
export function renderRegionSeriesSection(host: HTMLElement, ctx: RegionSeriesContext): void {
  host.replaceChildren()
  view = null
  if (!researchToolsAllowed()) {
    resetRegionSeries()
    if (!researchUpsellShown()) {
      host.hidden = true
      return
    }
    host.hidden = false
    const locked = button(t('research.rs.title'), 'analyze-action analyze-rs-open analyze-locked', (b) => openPlans(b, 'research', { notice: t('plans.locked.regionSeries') }))
    locked.setAttribute('aria-describedby', 'analyze-rs-lock')
    const why = para('analyze-lock-note', t('research.rs.locked'))
    why.id = 'analyze-rs-lock'
    host.append(locked, why)
    return
  }
  host.hidden = false
  const head = document.createElement('div')
  head.className = 'analyze-transect-head'
  const title = document.createElement('h3')
  title.textContent = t('research.rs.title')
  head.appendChild(title)
  host.appendChild(head)
  current = ctx
  if (releaseOf(ctx.frame.scale)?.kind === 'luma8-classified') {
    resetRegionSeries()
    host.appendChild(para('analyze-message', t('research.rs.classified')))
    return
  }
  host.appendChild(para('analyze-coverage analyze-rs-lead', t('research.rs.lead', { region: ctx.regionLabel })))
  if (!ctx.src.timeAxis?.()) {
    recorder?.stop?.()
    recorder = null
    host.appendChild(para('analyze-coverage analyze-rs-no-axis', t('research.rs.noAxis')))
    return
  }
  if (!armed) {
    recorder?.stop?.()
    recorder = null
    host.appendChild(button(t('research.rs.start'), 'analyze-action analyze-rs-start', () => {
      armed = true
      renderRegionSeriesSection(host, ctx)
      host.querySelector<HTMLElement>('.analyze-rs-chart')?.focus({ preventScroll: true })
    }))
    return
  }
  const rec = ensureRecorder(ctx)
  view = { host, status: null, plot: null, readout: null, stepBtn: null, when: null }
  const nowFrame = rec.axis?.currentFrame() ?? null
  if (rec.series && nowFrame !== null) recordThresholdFrame(rec.series, nowFrame, measureRegion(frameInput(ctx.frame.snapshot, rec)))
  if (!rec.series) {
    host.appendChild(para('analyze-coverage analyze-rs-no-axis', t('research.rs.noAxis')))
    return
  }
  const v = view
  const status = para('analyze-coverage analyze-thr-status analyze-rs-status', '')
  status.setAttribute('aria-live', 'polite')
  host.appendChild(status)
  v.status = status

  const plot = document.createElement('div')
  plot.className = 'tools-data-chart analyze-thr-chart analyze-rs-chart'
  plot.tabIndex = 0
  plot.setAttribute('role', 'group')
  plot.setAttribute('aria-roledescription', t('research.thr.roledescription'))
  plot.setAttribute('aria-label', t('research.rs.chartAria'))
  plot.setAttribute('aria-describedby', 'analyze-rs-readout')
  host.appendChild(plot)
  v.plot = plot
  const key = document.createElement('p')
  key.className = 'analyze-coverage analyze-rs-key'
  key.textContent = t('research.rs.key')
  host.appendChild(key)
  const readout = para('analyze-hov-readout analyze-thr-readout analyze-rs-readout', t('research.thr.readoutHint'))
  readout.id = 'analyze-rs-readout'
  readout.setAttribute('role', 'status')
  host.appendChild(readout)
  v.readout = readout
  const when = document.createElement('div')
  when.className = 'analyze-rs-when'
  when.hidden = true
  host.appendChild(when)
  v.when = when

  plot.addEventListener('pointermove', (e) => {
    const f = frameAtPointer(e)
    if (f !== null) showCursor(f)
  })
  plot.addEventListener('pointerleave', () => {
    if (document.activeElement !== plot) showCursor(null)
  })
  plot.addEventListener('click', (e) => {
    const f = frameAtPointer(e)
    if (f === null) return
    showCursor(f)
    seekFrame(f)
  })
  plot.addEventListener('keydown', onPlotKey)

  const actions = document.createElement('div')
  actions.className = 'analyze-thr-actions analyze-rs-actions'
  const stepBtn = button(t('research.thr.step'), 'analyze-action analyze-rs-step', () => {
    if (rec.stepping) {
      rec.cancel = !rec.cancel
      paintStatus(rec)
    } else void stepMissing(rec)
  })
  v.stepBtn = stepBtn
  actions.appendChild(stepBtn)
  actions.appendChild(button(t('research.rs.stop'), 'analyze-action analyze-rs-stop', () => {
    const ctxNow = current
    resetRegionSeries()
    if (ctxNow) {
      renderRegionSeriesSection(host, ctxNow)
      host.querySelector<HTMLElement>('.analyze-rs-start')?.focus()
    }
  }))
  actions.appendChild(button(t('research.thr.restart'), 'analyze-action analyze-rs-restart', () => {
    if (!rec.series) return
    rec.cancel = true
    rec.series.results.fill(null)
    rec.series.read = 0
    rec.series.lastFrame = -1
    cursor = null
    paintSeries()
  }))
  host.appendChild(actions)
  host.appendChild(para('analyze-coverage analyze-rs-resets', t('research.rs.resets')))
  if (!analysisExportsLocked()) {
    host.appendChild(button(t('research.rs.csv'), 'analyze-export analyze-rs-csv', () => {
      const part = regionCsv()
      if (part) downloadCsv(part.name, part.text)
    }))
  }
  const step = quantisationStep(rec.scale)
  host.appendChild(para('analyze-coverage analyze-rs-limits', step !== null
    ? t('research.rs.limits', { width: formatNumber(rec.width), height: formatNumber(rec.height), step: formatStatValue(step, rec.scale.units) })
    : t('research.rs.limitsUneven', { width: formatNumber(rec.width), height: formatNumber(rec.height) })))
  paintSeries()
  if (said && Date.now() - said.at < 4000) readout.textContent = said.text
  else if (cursor !== null) readout.textContent = describeFrame(rec, cursor)
}

/** The series as the button saves it; null without a series. */
function regionCsv(): { name: string; text: string } | null {
  const r = recorder
  const src = current?.src
  if (!r?.series || !src) return null
  trailRegion(r)
  const text = buildRegionCsvText(r.series, {
    datasetTitle: src.datasetTitle(),
    formatTime: (ms) => (researchFrameAxis() ? frameIsoForFile(ms) : null),
    units: r.scale.units,
    sourceUnits: r.scale.sourceUnits,
    region: r.regionEnglish,
    regionBounds: r.regionBounds,
    regionShape: r.regionShape,
    gridWidth: r.width,
    gridHeight: r.height,
    step: quantisationStep(r.scale),
    provenance: (src.spanProvenance?.() ?? src.provenance?.() ?? []).filter((row) => row[0] !== 'frame_time'),
  })
  return { name: ayniCsvFilename('region-series', src.datasetTitle(), new Date(), r.regionLabel), text }
}

/** AYNI: the region through time as a CSV for the reproducibility bundle; null without one or with exports locked. */
export function regionSeriesBundleCsv(): string | null {
  if (analysisExportsLocked()) return null
  if (!recorder?.series || recorder.series.read === 0) return null
  return regionCsv()?.text ?? null
}
